// Read the same complete primary-call set as MyVault's former browser filter,
// but never download other rangers' history to compute that set.
class ChangedHistory extends Error {}
const rangerOf = session => session?.profile?.rangerId?.trim().toUpperCase();
export const visibleVaultHistory = (state, profile) => state.ownerUserId === Number(profile?.id) &&
  state.rangerId === profile?.rangerId?.trim().toUpperCase() ? state.items : [];
export async function loadVaultHistory({ session, currentSession, fetchImpl = fetch, nonce, pageSize = 200, signal }) {
  const ranger = rangerOf(session);
  if (!session || session.expired || !ranger) throw new Error('Sign in with a Ranger ID to load your call history.');
  if (!nonce || !Number.isInteger(pageSize) || pageSize < 1 || pageSize > 200) throw new Error('History request is invalid.');
  const cancelled = () => Object.assign(new Error('Call history read cancelled.'), { code: 'ERR_CANCELED' });
  const checkSession = () => {
    if (signal?.aborted) throw cancelled();
    const active = currentSession();
    if (!active || active.token !== session.token || active.ownerUserId !== session.ownerUserId || rangerOf(active) !== ranger)
      throw new Error('Account changed while loading history.');
    if (active.expired) throw new Error('Sign in again to load your call history. Previously loaded calls were retained.');
  };
  const readPage = async params => {
    checkSession();
    const controller = new AbortController();
    const abort = () => controller.abort();
    signal?.addEventListener('abort', abort, { once: true });
    const timer = setTimeout(abort, 20000);
    const checkRead = () => {
      checkSession();
      if (controller.signal.aborted) throw new Error('Call history request timed out. Retry to refresh; previously loaded calls were retained.');
    };
    try {
      checkRead();
      const response = await fetchImpl('/api/incidents?' + params, { headers: { Authorization: `Bearer ${session.token}` },
        cache: 'no-store', redirect: 'error', signal: controller.signal });
      checkRead();
      if (response.status !== 200) throw new Error(response.status === 401 ? 'Sign in again to load your call history.' : 'Unable to load call history. Previously loaded calls were retained.');
      const body = await response.json(); checkRead(); return body;
    } catch (error) { checkRead(); throw error; }
    finally { clearTimeout(timer); signal?.removeEventListener('abort', abort); }
  };
  for (let attempt = 0; attempt < 2; attempt++) {
    const rows = [], seen = new Set(); let total = null, page = 1;
    try {
      do {
        checkSession();
        const params = new URLSearchParams({ primaryRangerId: ranger, page: String(page), pageSize: String(pageSize), __cad_read: `${nonce}-${attempt}` });
        const body = await readPage(params);
        if (body?.primaryRangerId !== ranger || !Array.isArray(body.items) || !Number.isSafeInteger(body.total) || body.total < 0)
          throw new Error('The server did not confirm complete primary-ranger history. Previously loaded calls were retained.');
        if (total !== null && total !== body.total) throw new ChangedHistory();
        total = body.total;
        if (body.items.length > pageSize) throw new ChangedHistory();
        for (const row of body.items) {
          if (!Number.isSafeInteger(row?.id) || row.id <= 0 || row.primaryRangerId?.trim().toUpperCase() !== ranger)
            throw new Error('Unexpected call history response. Previously loaded calls were retained.');
          if (seen.has(row.id)) throw new ChangedHistory();
          seen.add(row.id); rows.push(row);
        }
        if (rows.length > total || (rows.length < total && body.items.length !== pageSize)) throw new ChangedHistory();
        page++;
      } while (rows.length < total);
      return rows;
    } catch (error) {
      if (!(error instanceof ChangedHistory)) throw error;
      if (attempt === 1) throw new Error('Call history changed while loading. Retry to refresh; previously loaded calls were retained.');
    }
  }
}
